import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Settings, Loader2, X, TrendingUp, BookOpen, Database, Clock } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * IntegrationsTab Component
 * Allows dealers to connect their API accounts (MMR, Black Book, Chrome Data)
 * @param {Object} props - Component props
 * @param {Object} props.theme - Theme object with styling properties
 * @param {Object} props.colors - Color palette object
 * @param {Function} props.showToast - Function to show toast notifications
 */
export const IntegrationsTab = ({ theme, colors, showToast }) => {
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({
    apiKey: '',
    apiSecret: '',
    accountId: '',
    enabled: true
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dealerId] = useState('test-dealer'); // In production, get from auth context

  // Integration providers configuration
  const providers = [
    {
      id: 'mmr',
      name: 'MMR (Manheim)',
      description: 'Manheim Market Report - Wholesale vehicle valuations',
      icon: TrendingUp,
      requiresSecret: true,
      requiresAccountId: true
    },
    {
      id: 'blackBook',
      name: 'Black Book',
      description: 'Black Book - Vehicle valuation data',
      icon: BookOpen,
      requiresSecret: false,
      requiresAccountId: false
    },
    {
      id: 'chromeData',
      name: 'Chrome Data',
      description: 'Chrome Data / NADA - Vehicle specifications and valuations',
      icon: Database,
      requiresSecret: false,
      requiresAccountId: false
    }
  ];

  // Fetch integrations on mount
  useEffect(() => {
    fetchIntegrations();
  }, []);

  /**
   * Fetch dealer integrations from API
   */
  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/integrations/${dealerId}`);
      
      if (response.status === 404) {
        // No integrations found yet, that's okay
        setIntegrations(null);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch integrations');
      }
      
      const data = await response.json();
      setIntegrations(data.integration);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      showToast('Failed to load integrations', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open connect modal for a provider
   */
  const handleConnect = (provider) => {
    const providerData = integrations?.[provider.id];
    setSelectedProvider(provider);
    setFormData({
      apiKey: '',
      apiSecret: '',
      accountId: providerData?.accountId || '',
      enabled: providerData?.enabled || true
    });
    setShowModal(true);
  };

  /**
   * Close modal and reset form
   */
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProvider(null);
    setFormData({
      apiKey: '',
      apiSecret: '',
      accountId: '',
      enabled: true
    });
  };

  /**
   * Save integration credentials
   */
  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        provider: selectedProvider.id,
        enabled: formData.enabled,
        apiKey: formData.apiKey
      };

      if (selectedProvider.requiresSecret) {
        payload.apiSecret = formData.apiSecret;
      }

      if (selectedProvider.requiresAccountId) {
        payload.accountId = formData.accountId;
      }

      const response = await fetch(`${API_BASE_URL}/api/integrations/${dealerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save integration');
      }

      showToast(`${selectedProvider.name} connected successfully`, 'success');
      handleCloseModal();
      await fetchIntegrations();
    } catch (error) {
      console.error('Error saving integration:', error);
      showToast(error.message || 'Failed to save integration', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Test integration connection
   */
  const handleTest = async (providerId) => {
    try {
      setTesting(true);
      const response = await fetch(`${API_BASE_URL}/api/integrations/${dealerId}/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider: providerId })
      });

      const data = await response.json();

      if (data.success) {
        showToast(`Successfully connected to ${providers.find(p => p.id === providerId)?.name}`, 'success');
      } else {
        showToast(data.message || 'Connection test failed', 'error');
      }
    } catch (error) {
      console.error('Error testing integration:', error);
      showToast('Failed to test connection', 'error');
    } finally {
      setTesting(false);
    }
  };

  /**
   * Disconnect integration
   */
  const handleDisconnect = async (providerId) => {
    if (!window.confirm(`Are you sure you want to disconnect ${providers.find(p => p.id === providerId)?.name}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/integrations/${dealerId}/${providerId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect integration');
      }

      showToast(`${providers.find(p => p.id === providerId)?.name} disconnected`, 'success');
      await fetchIntegrations();
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      showToast('Failed to disconnect integration', 'error');
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  /**
   * Get provider status
   */
  const getProviderStatus = (providerId) => {
    const providerData = integrations?.[providerId];
    if (!providerData || !providerData.enabled) {
      return { connected: false, lastSync: null };
    }
    return {
      connected: true,
      lastSync: providerData.lastSync
    };
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Loader2 size={32} color={colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px' }}>
      <div style={{
        marginBottom: '40px',
        borderBottom: `1px solid ${theme.border}`,
        paddingBottom: '20px'
      }}>
        <h2 style={{
          color: theme.text,
          fontSize: '24px',
          fontWeight: '600',
          margin: 0,
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          letterSpacing: '-0.02em'
        }}>
          <Settings size={24} color={colors.primary} />
          API Integrations
        </h2>
        <p style={{
          color: theme.textSecondary,
          fontSize: '14px',
          margin: 0,
          lineHeight: '1.5',
          fontWeight: '400'
        }}>
          Connect your vehicle valuation API accounts to enable automatic pricing
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
        gap: '24px'
      }}>
        {providers.map((provider) => {
          const status = getProviderStatus(provider.id);
          const IconComponent = provider.icon;
          
          return (
            <div
              key={provider.id}
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
                backdropFilter: 'blur(40px) saturate(150%)',
                WebkitBackdropFilter: 'blur(40px) saturate(150%)',
                border: status.connected 
                  ? '1px solid rgba(16, 185, 129, 0.3)' 
                  : '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '24px',
                boxShadow: status.connected 
                  ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(16, 185, 129, 0.2)'
                  : '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(59, 130, 246, 0.1)',
                padding: '32px',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = status.connected 
                  ? 'rgba(16, 185, 129, 0.4)' 
                  : 'rgba(59, 130, 246, 0.3)';
                e.currentTarget.style.boxShadow = status.connected
                  ? '0 12px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 100px rgba(16, 185, 129, 0.3)'
                  : '0 12px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 100px rgba(59, 130, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = status.connected 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.boxShadow = status.connected 
                  ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(16, 185, 129, 0.2)'
                  : '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(59, 130, 246, 0.1)';
              }}
            >
              {/* Top highlight */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: '10%',
                right: '10%',
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
              }} />
              
              {/* Inner glow */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: status.connected
                  ? 'radial-gradient(circle at 30% 20%, rgba(16, 185, 129, 0.08), transparent 50%)'
                  : 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.08), transparent 50%)',
                pointerEvents: 'none'
              }} />
              {/* Provider Header */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '10px',
                  background: status.connected 
                    ? `linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)`
                    : `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}08 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${status.connected ? 'rgba(16, 185, 129, 0.2)' : theme.border}`,
                  flexShrink: 0
                }}>
                  <IconComponent 
                    size={24} 
                    color={status.connected ? colors.success : colors.primary}
                    strokeWidth={2}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    color: theme.text,
                    fontSize: '16px',
                    fontWeight: '600',
                    margin: 0,
                    marginBottom: '6px',
                    letterSpacing: '-0.01em',
                    lineHeight: '1.3'
                  }}>
                    {provider.name}
                  </h3>
                  <p style={{
                    color: theme.textSecondary,
                    fontSize: '13px',
                    margin: 0,
                    lineHeight: '1.5',
                    fontWeight: '400'
                  }}>
                    {provider.description}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                padding: '8px 12px',
                borderRadius: '6px',
                background: status.connected 
                  ? 'rgba(16, 185, 129, 0.1)' 
                  : 'rgba(107, 114, 128, 0.1)',
                border: `1px solid ${status.connected ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.15)'}`
              }}>
                {status.connected ? (
                  <>
                    <CheckCircle size={16} color={colors.success} strokeWidth={2.5} />
                    <span style={{
                      color: colors.success,
                      fontSize: '13px',
                      fontWeight: '500',
                      letterSpacing: '0.01em'
                    }}>
                      Connected
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle size={16} color="#6b7280" strokeWidth={2.5} />
                    <span style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontWeight: '500',
                      letterSpacing: '0.01em'
                    }}>
                      Not Connected
                    </span>
                  </>
                )}
              </div>

              {/* Last Sync */}
              {status.connected && (
                <div style={{
                  marginBottom: '20px',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  background: theme.bgSecondary || 'rgba(107, 114, 128, 0.05)',
                  border: `1px solid ${theme.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Clock size={14} color={theme.textSecondary} strokeWidth={2} />
                  <span style={{
                    color: theme.textSecondary,
                    fontSize: '12px',
                    fontWeight: '400'
                  }}>
                    Last sync: {formatDate(status.lastSync)}
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '4px'
              }}>
                {!status.connected ? (
                  <button
                    type="button"
                    onClick={() => handleConnect(provider)}
                    style={{
                      flex: 1,
                      padding: '11px 20px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.4)';
                    }}
                  >
                    Connect
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleTest(provider.id)}
                      disabled={testing}
                      style={{
                        flex: 1,
                        padding: '11px 16px',
                        borderRadius: '12px',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        background: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: testing ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: testing ? 0.6 : 1,
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                      }}
                      onMouseEnter={(e) => {
                        if (!testing) {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                          e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!testing) {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {testing ? (
                        <>
                          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          Testing...
                        </>
                      ) : (
                        'Test Connection'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDisconnect(provider.id)}
                      style={{
                        flex: 1,
                        padding: '11px 16px',
                        borderRadius: '12px',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(239, 68, 68, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      Disconnect
                    </button>
                    <button
                      type="button"
                      onClick={() => handleConnect(provider)}
                      style={{
                        padding: '11px',
                        borderRadius: '8px',
                        border: `1px solid ${theme.border}`,
                        background: 'transparent',
                        color: theme.textSecondary,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                      title="Update credentials"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme.bgSecondary || 'rgba(107, 114, 128, 0.08)';
                        e.currentTarget.style.borderColor = colors.primary;
                        e.currentTarget.style.color = colors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = theme.border;
                        e.currentTarget.style.color = theme.textSecondary;
                      }}
                    >
                      <Settings size={16} strokeWidth={2} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Connect Modal */}
      {showModal && selectedProvider && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(10, 14, 39, 0.9)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            ...theme.glass,
            padding: '32px',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            border: `1px solid ${theme.border}`,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '28px',
              paddingBottom: '20px',
              borderBottom: `1px solid ${theme.border}`
            }}>
              <h2 style={{
                color: theme.text,
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                letterSpacing: '-0.01em'
              }}>
                {(() => {
                  const IconComponent = selectedProvider.icon;
                  return <IconComponent size={22} color={colors.primary} strokeWidth={2} />;
                })()}
                Connect {selectedProvider.name}
              </h2>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.text,
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  color: theme.text,
                  fontSize: '13px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  letterSpacing: '0.01em'
                }}>
                  API Key *
                </label>
                <input
                  type="password"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  placeholder="Enter your API key"
                  required
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.border}`,
                    background: theme.bgSecondary || theme.glass.background,
                    color: theme.text,
                    fontSize: '14px',
                    fontWeight: '400',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primary}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              {selectedProvider.requiresSecret && (
                <div>
                  <label style={{
                    display: 'block',
                    color: theme.text,
                    fontSize: '13px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    letterSpacing: '0.01em'
                  }}>
                    API Secret *
                  </label>
                  <input
                    type="password"
                    value={formData.apiSecret}
                    onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
                    placeholder="Enter your API secret"
                    required
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      background: theme.bgSecondary || theme.glass.background,
                      color: theme.text,
                      fontSize: '14px',
                      fontWeight: '400',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = colors.primary;
                      e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primary}15`;
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = theme.border;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>
              )}

              {selectedProvider.requiresAccountId && (
                <div>
                  <label style={{
                    display: 'block',
                    color: theme.text,
                    fontSize: '13px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    letterSpacing: '0.01em'
                  }}>
                    Account ID *
                  </label>
                  <input
                    type="text"
                    value={formData.accountId}
                    onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                    placeholder="Enter your account ID"
                    required
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      background: theme.bgSecondary || theme.glass.background,
                      color: theme.text,
                      fontSize: '14px',
                      fontWeight: '400',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = colors.primary;
                      e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primary}15`;
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = theme.border;
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '11px 20px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.border}`,
                    background: 'transparent',
                    color: theme.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.background = theme.bgSecondary || 'rgba(107, 114, 128, 0.08)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '11px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary || colors.primary}dd 100%)`,
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: `0 2px 4px ${colors.primary}30`
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = `0 4px 12px ${colors.primary}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 2px 4px ${colors.primary}30`;
                    }
                  }}
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

